import useFacebookPixel from "@/utils/helpers/pixel-helpers/hooks/useFacebookPixel";
import useSnapchatPixel from "@/utils/helpers/pixel-helpers/hooks/useSnapchatPixel";
import {
  Gtag,
  GTagMaillistConversionEvent,
  GTagSearchEvent,
  GTagUserLoginEvent,
} from "../analytics";
import { ICartItemType } from "@/api/AppApi/services/cart/interfaces/cart-interfaces";
import {
  EventType,
  FacebookActionField,
  GtagActionField,
  IGTagUserData,
  SnapchatActionField,
  TiktokActionField,
  Tracker,
} from "./helpers/interfaces";
import { getEventNameForTracker } from "./helpers/functions";
import { useGetCompanyQuery } from "@/api/AppApi/services/company";
import { useCurrencyCalc } from "@/hooks/useCurrencyCalc";
import useTiktokPixel from "../useTiktokPixel";

const useEventTrackers = () => {
  const company = useGetCompanyQuery();
  const { currency, getPriceString } = useCurrencyCalc({
    enable:
      company?.data?.result?.theme?.settings?.enable_currencies_converter ??
      true,
  });
  const { name } = company?.data?.result ?? {};
  const snapchatEvent = useSnapchatPixel();
  const facebookEvent = useFacebookPixel();
  const tiktokEvent = useTiktokPixel();

  const eventTrackers = (
    event: EventType,
    items: ICartItemType[],
    data: GtagActionField,
    userdata?: IGTagUserData,
  ) => {
    const SnapChatEventData: SnapchatActionField = {
      currency: currency.code!,
      price: +getPriceString(data?.value!),
      transaction_id: data?.transaction_id,
      item_ids: items?.map((item) => item?.inner_id?.toString()),
      number_items: items?.length,
    };
    if (!!userdata) {
      const { firstName, lastName } = getFirstAndLastName(userdata.name ?? "");
      SnapChatEventData["user_email"] = userdata.email;
      SnapChatEventData["user_hashed_email"] = userdata.email_hash;
      SnapChatEventData["user_phone_number"] = userdata.phone;
      SnapChatEventData["user_hashed_phone_number"] = userdata.phone_hash;
      SnapChatEventData["firstname"] = firstName;
      SnapChatEventData["lastname"] = lastName;
    }
    const facebookEventData: FacebookActionField = {
      currency: currency.code!,
      value: +getPriceString(data?.value!),
      content_ids: items?.map((item) => item?.inner_id?.toString()),
      content_type: "product",
    };
    const tiktokEventData: TiktokActionField = {
      currency: currency.code!,
      value: +getPriceString(data?.value!),
      content_ids: items?.map((item) => item?.inner_id?.toString()),
      content_type: "product",
    };
    if (
      ![EventType.remove_from_cart, EventType.add_shipping_info].includes(event)
    )
      snapchatEvent.trackEvent(
        getEventNameForTracker(event, Tracker.SCP),
        SnapChatEventData,
      );

    facebookEvent.trackEvent(
      getEventNameForTracker(event, Tracker.FBP),
      facebookEventData,
    );
    tiktokEvent.trackEvent(
      getEventNameForTracker(event, Tracker.TIK),
      tiktokEventData,
    );
    Gtag(
      getEventNameForTracker(event, Tracker.GTAG),
      items,
      data,
      name ?? "",
      getPriceString,
      currency.code,
    );
  };
  const GTagUserLoginEventTracher = (
    isSignedIn: boolean,
    userdata: IGTagUserData,
  ) => {
    return GTagUserLoginEvent(isSignedIn, userdata);
  };
  const GTagSearchEventTracher = (search: string, is_found: boolean) => {
    return GTagSearchEvent(search, is_found ? "found" : "not found");
  };
  const GTagMaillistConversionEventTracher = (email: string) => {
    return GTagMaillistConversionEvent(email);
  };
  return {
    eventTrackers,
    GTagUserLoginEventTracher,
    GTagMaillistConversionEventTracher,
    GTagSearchEventTracher,
  };
};

export default useEventTrackers;

function getFirstAndLastName(fullName: string): {
  firstName: string;
  lastName: string;
} {
  if (!fullName || typeof fullName !== "string") {
    return { firstName: "", lastName: "" };
  }

  // Trim and clean up the name (remove extra spaces)
  const cleanedName = fullName.trim().replace(/\s+/g, " ");
  const nameParts = cleanedName.split(" ");

  // Handle empty string case
  if (nameParts.length === 0) {
    return { firstName: "", lastName: "" };
  }

  // If only one name part, it's both first and last
  if (nameParts.length === 1) {
    return { firstName: nameParts[0], lastName: nameParts[0] };
  }

  // For multiple parts, first is first part, last is last part
  const firstName = nameParts[0];
  const lastName = nameParts[nameParts.length - 1];

  return { firstName, lastName };
}
